import React from 'react';
import { Button } from './Button';

// LoadErrorState - the one way a failed load is shown (V2-020; DESIGN.md STATE-02).
//
// A plain sentence – "Couldn't load purchase orders. Try again." – with a Retry button. Never the
// raw error, never "No [records] found", never a count of 0: a failed request is not an empty
// result. The detail is already logged (Bugsnag); nothing from the error body is shown.
//
// V3-021: Retry is always visible, on every tile size. Compact (Home tiles) stacks a short message
// (at most two lines, full text as the tooltip) above the button, so a narrow tile can't clip it.

export interface LoadErrorStateProps {
  /** What failed to load, lower case: "purchase orders", "activities", "this tile". */
  what: string;
  /** Runs the load again. Without it the message is shown alone. */
  onRetry?: () => void;
  /** Smaller text and button, for Home tiles. */
  compact?: boolean;
  /** Replaces the standard sentence, e.g. "Couldn't load this chart." */
  message?: string;
  className?: string;
}

export function loadErrorMessage(what: string): string {
  return `Couldn't load ${what}. Try again.`;
}

export const LoadErrorState: React.FC<LoadErrorStateProps> = ({ what, onRetry, compact = false, message, className = '' }) => {
  const text = message ?? (compact ? `Couldn't load ${what}.` : loadErrorMessage(what));
  return (
    <div
      role="alert"
      data-testid="load-error"
      className={`tw-flex ${
        compact
          ? 'tw-min-w-0 tw-flex-col tw-items-start tw-gap-1 tw-pt-1 tw-text-xs'
          : 'tw-flex-wrap tw-items-center tw-gap-2 tw-rounded tw-border tw-border-danger/40 tw-bg-danger/5 tw-p-3'
      } tw-text-danger ${className}`}
    >
      <span className={compact ? 'tw-line-clamp-2 tw-max-w-full tw-leading-snug' : undefined} title={compact ? text : undefined}>
        {text}
      </span>
      {onRetry && (
        <Button
          type="button"
          size="sm"
          variant="outline-secondary"
          className="tw-shrink-0"
          onClick={(event: React.MouseEvent) => {
            // A Home tile opens its page on click; Retry only retries.
            event.stopPropagation();
            onRetry();
          }}
        >
          Retry
        </Button>
      )}
    </div>
  );
};

export default LoadErrorState;
